import MenuIcon from "@mui/icons-material/Menu";
import {
  AppBar,
  Avatar,
  Box,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
} from "@mui/material";
import React, { useMemo } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import Logo from "../../assets/icons/logo";
import { AppRoutes } from "../../constants";
import useAuth from "../../hooks/useAuth";
import { UserRole } from "../../types";
import { IFile } from "../../types/auth";
import { menuItems, profileMenuItems } from "./components/menuItems";
import styles from "./organization-header.module.css";

interface Props {
  window?: () => Window;
}

const ActiveNavLink = ({ to, children, isHome = false }: any) => (
  <NavLink
    to={to}
    end={isHome}
    className={({ isActive }) =>
      isActive ? `${styles.link} ${styles.activeLink}` : styles.link
    }
  >
    {children}
  </NavLink>
);

const drawerWidth = 240;

const OrganizationHeader = (props: Props) => {
  const { window } = props;
  const {
    state: { user, userOrganization },
  } = useAuth();
  const [mobileOpen, setMobileOpen] = React.useState(false);

  const userLogo = useMemo(() => {
    return userOrganization?.logo;
  }, [userOrganization?.logo]) as IFile;

  const handleDrawerToggle = () => {
    setMobileOpen((prev) => !prev);
  };

  const isSuperAdmin = useMemo(
    () => user?.role === UserRole.SUPER_ADMIN,
    [user]
  );

  const navItems = useMemo(() => {
    if (!user?.role) return [];

    return menuItems.filter((item) =>
      item.permission?.includes(user.role as UserRole)
    );
  }, [user?.role]);

  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ textAlign: "center" }}>
      <Box className="flex-box" height={"60px"}>
        {isSuperAdmin ? (
          <h2>SUPER ADMIN</h2>
        ) : userLogo ? (
          <Box sx={{ height: "60px", display: "flex", alignItems: "center" }}>
            <img
              src={userLogo?.url}
              alt="Organization Logo"
              style={{
                maxHeight: "50px",
                maxWidth: "180px",
                objectFit: "contain",
              }}
            />
          </Box>
        ) : (
          <Logo />
        )}
      </Box>
      <Divider />
      <Box sx={{ display: "flex", flexDirection: "column", mt: 2, gap: 1 }}>
        {navItems.map(({ title, path }) => (
          <ActiveNavLink
            key={path}
            to={path}
            isHome={
              path === AppRoutes.ORGANIZATION.HOME ||
              path === AppRoutes.ADMIN.HOME
            }
          >
            {title}
          </ActiveNavLink>
        ))}
      </Box>
    </Box>
  );

  const container =
    window !== undefined ? () => window().document.body : undefined;

  return (
    <Box sx={{ display: "flex", height: "70px" }}>
      <CssBaseline />
      <AppBar component="nav" sx={{ height: "70px" }}>
        <Toolbar sx={{ height: "70px" }}>
          <Box className={styles.headerContent}>
            {isSuperAdmin ? (
              <h2>SUPER ADMIN</h2>
            ) : userLogo ? (
              <Box
                sx={{ height: "60px", display: "flex", alignItems: "center" }}
              >
                <img
                  src={userLogo?.url}
                  alt="Organization Logo"
                  className={styles.logo}
                />
              </Box>
            ) : (
              <Logo />
            )}

            {/* Desktop Navigation */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 4 }}>
              <Box sx={{ display: { xs: "none", sm: "flex" }, gap: 3 }}>
                {navItems.map(({ title, path }) => (
                  <ActiveNavLink
                    key={path}
                    to={path}
                    isHome={
                      path === AppRoutes.ORGANIZATION.HOME ||
                      path === AppRoutes.ADMIN.HOME
                    }
                  >
                    {title}
                  </ActiveNavLink>
                ))}
              </Box>

              <Box sx={{ display: { xs: "none", sm: "block" } }}>
                <ProfileMenu />
              </Box>
            </Box>

            {/* Mobile Menu Button + Avatar */}
            <Box
              sx={{
                display: { xs: "flex", sm: "none" },
                alignItems: "center",
                gap: 1,
              }}
            >
              <IconButton
                color="inherit"
                aria-label="open drawer"
                onClick={handleDrawerToggle}
              >
                <MenuIcon />
              </IconButton>
              <ProfileMenu />
            </Box>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      <nav>
        <Drawer
          container={container}
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: "block", sm: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>
      </nav>
    </Box>
  );
};

// ProfileMenu stays the same
const ProfileMenu = () => {
  const navigate = useNavigate();
  const {
    state: { user },
    userLogout,
  } = useAuth();
  const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(
    null
  );

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const visibleMenuItems = React.useMemo(() => {
    return profileMenuItems.filter((item) => {
      if (!item.roles) return true;
      return item.roles.includes(user?.role as UserRole);
    });
  }, [user?.role]);

  return (
    <Box>
      <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
        <Avatar>{user?.name?.[0]?.toUpperCase()}</Avatar>
      </IconButton>
      <Menu
        sx={{ mt: "45px" }}
        anchorEl={anchorElUser}
        open={Boolean(anchorElUser)}
        onClose={handleCloseUserMenu}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem>
          <Typography>{user?.name}</Typography>
        </MenuItem>
        <Divider />
        {visibleMenuItems.map((item) => (
          <MenuItem
            key={item.label}
            onClick={() => {
              handleCloseUserMenu();

              if (item.action === "logout") {
                userLogout();
                return;
              }

              if (item.path) {
                navigate(item.path);
              }
            }}
          >
            <Typography>{item.label}</Typography>
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export default OrganizationHeader;
